/** 角色素材库：场景 / 用途预设（与 n8n/lib/material_scenes.js 语义同步） */

export type MaterialType = 'photo' | 'silent_video' | 'talking_video' | 'voice' | 'lead_hook_asset';
export type SceneType =
  | 'friend_candid'
  | 'travel'
  | 'daily_life'
  | 'work'
  | 'cafe'
  | 'street'
  | 'home'
  | 'friend_candid_video';

export type PhotoSubtype =
  | 'life'
  | 'street_snap'
  | 'friend_angle'
  | 'cafe'
  | 'travel'
  | 'meal'
  | 'shopping'
  | 'work_daily'
  | 'selfie'
  | 'mirror_selfie'
  | 'back_view'
  | 'half_body'
  | 'side_profile';

export type SilentMotion =
  | 'walk'
  | 'phone'
  | 'look_back'
  | 'fix_hair'
  | 'drink'
  | 'scenery'
  | 'car_window'
  | 'bag'
  | 'wait'
  | 'mall';

export const SCENE_LABELS: Record<SceneType, string> = {
  friend_candid: '朋友偷拍感',
  travel: '出游',
  daily_life: '日常生活',
  work: '工作日常',
  cafe: '咖啡店',
  street: '街边',
  home: '家里',
  friend_candid_video: '朋友路拍视频',
};

export const PHOTO_SUBTYPE_LABELS: Record<PhotoSubtype, string> = {
  life: '生活照',
  street_snap: '路拍照',
  friend_angle: '朋友视角',
  cafe: '咖啡店',
  travel: '出游照',
  meal: '吃饭',
  shopping: '逛街',
  work_daily: '工作日常',
  selfie: '手机自拍',
  mirror_selfie: '镜子自拍',
  back_view: '背影照',
  half_body: '半身照',
  side_profile: '侧脸照',
};

export const SILENT_MOTION_LABELS: Record<SilentMotion, string> = {
  walk: '自然走路',
  phone: '低头看手机',
  look_back: '回头看镜头',
  fix_hair: '整理头发',
  drink: '拿饮料',
  scenery: '看风景',
  car_window: '坐车窗边',
  bag: '整理包包',
  wait: '街边等人',
  mall: '商场逛街',
};

export const USAGE_PURPOSE_LABELS: Record<string, string> = {
  moments: '发朋友圈',
  douyin: '发抖音',
  xiaohongshu: '发小红书',
  dm_icebreaker: '私信破冰前铺垫',
  comment_warmup: '评论暖场',
  reply_client: '回复客户时使用',
  lead_magnet: '引导对方领资料',
  build_trust: '建立真人感',
};

export const USAGE_OPTIONS = [
  { value: 'moments', label: USAGE_PURPOSE_LABELS.moments },
  { value: 'douyin', label: USAGE_PURPOSE_LABELS.douyin },
  { value: 'xiaohongshu', label: USAGE_PURPOSE_LABELS.xiaohongshu },
  { value: 'dm_icebreaker', label: USAGE_PURPOSE_LABELS.dm_icebreaker },
  { value: 'comment_warmup', label: USAGE_PURPOSE_LABELS.comment_warmup },
  { value: 'build_trust', label: USAGE_PURPOSE_LABELS.build_trust },
] as const;

export const MATERIAL_TYPE_LABELS: Record<MaterialType, string> = {
  photo: '角色照片',
  silent_video: '无声生活影片',
  talking_video: '口播影片',
  voice: '声音',
  lead_hook_asset: '深度引流钩子',
};

export function buildPhotoIntent(scene: SceneType, subtype: PhotoSubtype): string {
  const map: Record<PhotoSubtype, string> = {
    life: '日常生活照，像平时随手拍',
    street_snap: '路边随手拍，自然走路或站着',
    friend_angle: '朋友在旁边随手拍到的生活照，微微不完美',
    cafe: '咖啡店坐着，自然放松',
    travel: '出游时在景点或路上的生活照',
    meal: '吃饭时的日常照，桌上有点食物',
    shopping: '逛街时的半身照',
    work_daily: '工作/办公日常，自然状态',
    selfie: '手机自拍，半身，自然表情',
    mirror_selfie: '镜子前自拍，日常穿搭',
    back_view: '背影照，自然场景',
    half_body: '半身照，头到腰，生活场景',
    side_profile: '侧脸照，自然光线',
  };
  return `${SCENE_LABELS[scene]} · ${map[subtype]}`;
}

export function buildSilentVideoIntent(scene: SceneType, motion: SilentMotion): string {
  return `${SCENE_LABELS[scene]} · ${SILENT_MOTION_LABELS[motion]}，像朋友在旁边随手录到的生活片段，无配音`;
}

/** 规格 §9 照片类型卡片 */
export interface PhotoMaterialCard {
  id: PhotoSubtype;
  scene: SceneType;
  title: string;
  description: string;
  purposes: string[];
  targetUser: string;
  defaultCount: number;
}

export const PHOTO_MATERIAL_CARDS: PhotoMaterialCard[] = [
  { id: 'friend_angle', scene: 'friend_candid', title: '朋友路拍照', description: '像朋友出游时在旁边随手拍到，不是精修大片。', purposes: ['朋友圈铺垫', '建立真人感', '私信前破冰'], targetUser: '有服务但主页说不清的人', defaultCount: 3 },
  { id: 'travel', scene: 'travel', title: '出游生活照', description: '出游路上的自然状态，像平时随手拍。', purposes: ['朋友圈', '建立生活感'], targetUser: '想先建立信任的人', defaultCount: 2 },
  { id: 'cafe', scene: 'cafe', title: '咖啡店日常照', description: '坐着喝咖啡或等人，放松自然。', purposes: ['朋友圈铺垫', '私信破冰前铺垫'], targetUser: '偏文艺、生活感受众', defaultCount: 2 },
  { id: 'life', scene: 'daily_life', title: '低头看手机照', description: '低头看手机或回消息的日常瞬间。', purposes: ['降低推销感', '建立真人感'], targetUser: '对硬广敏感的人', defaultCount: 2 },
  { id: 'work_daily', scene: 'work', title: '工作日常照', description: '办公或工作场景，自然不摆拍。', purposes: ['专业信任', '朋友圈'], targetUser: 'B端或服务业客户', defaultCount: 2 },
  { id: 'meal', scene: 'daily_life', title: '吃饭聊天照', description: '吃饭时自然聊天，桌上有点食物。', purposes: ['生活感', '朋友圈'], targetUser: '泛人群', defaultCount: 2 },
  { id: 'street_snap', scene: 'street', title: '街边等人照', description: '街边站着等人，自然放松。', purposes: ['破冰铺垫', '朋友圈'], targetUser: '陌生人私信前', defaultCount: 2 },
  { id: 'selfie', scene: 'friend_candid', title: '回头笑一下', description: '走路时回头看镜头笑一下。', purposes: ['拉近距离', '私信破冰'], targetUser: '偏年轻受众', defaultCount: 2 },
  { id: 'mirror_selfie', scene: 'home', title: '镜子自拍', description: '镜子前半身自拍，日常穿搭。', purposes: ['朋友圈', '建立真人感'], targetUser: '私域熟客', defaultCount: 1 },
  { id: 'half_body', scene: 'daily_life', title: '半身生活照', description: '头到腰半身，生活场景。', purposes: ['主页配图', '朋友圈'], targetUser: '泛人群', defaultCount: 2 },
  { id: 'back_view', scene: 'travel', title: '背影照', description: '自然场景下的背影，不刻意摆拍。', purposes: ['氛围感', '短视频封面'], targetUser: '偏文艺受众', defaultCount: 1 },
  { id: 'side_profile', scene: 'street', title: '侧脸照', description: '侧脸自然光线，不像模特大片。', purposes: ['建立真人感'], targetUser: '泛人群', defaultCount: 1 },
];

/** 规格 §11 无声影片类型卡片 */
export interface SilentVideoCard {
  motion: SilentMotion;
  scene: SceneType;
  title: string;
  description: string;
  purposes: string[];
  targetUser: string;
}

export const SILENT_VIDEO_CARDS: SilentVideoCard[] = [
  { motion: 'walk', scene: 'friend_candid_video', title: '朋友路拍无声影片', description: '像朋友在旁边随手录到的 5-8 秒生活片段，没有声音。', purposes: ['朋友圈', '抖音背景', '私信破冰前铺垫'], targetUser: '有服务但主页说不清的人' },
  { motion: 'walk', scene: 'travel', title: '出游走路影片', description: '出游时自然走路，手持轻微晃动。', purposes: ['短视频背景', '建立生活感'], targetUser: '想降低推销感时' },
  { motion: 'phone', scene: 'daily_life', title: '低头看手机影片', description: '低头看手机或回消息的日常片段。', purposes: ['破冰铺垫'], targetUser: '对硬广敏感的人' },
  { motion: 'drink', scene: 'cafe', title: '咖啡店坐着影片', description: '坐着喝咖啡，自然放松。', purposes: ['朋友圈', '私信前铺垫'], targetUser: '偏生活感受众' },
  { motion: 'wait', scene: 'street', title: '街边等人影片', description: '街边站着等人，自然状态。', purposes: ['破冰', '朋友圈'], targetUser: '陌生人私信前' },
  { motion: 'bag', scene: 'street', title: '整理包包影片', description: '整理包包或拿东西的日常动作。', purposes: ['生活感'], targetUser: '泛人群' },
  { motion: 'look_back', scene: 'friend_candid_video', title: '回头笑一下影片', description: '走路时回头看镜头笑一下。', purposes: ['拉近距离'], targetUser: '偏年轻受众' },
  { motion: 'drink', scene: 'cafe', title: '拿饮料影片', description: '拿起饮料喝一口或拿着走。', purposes: ['生活感', '朋友圈'], targetUser: '泛人群' },
  { motion: 'car_window', scene: 'travel', title: '坐车窗边影片', description: '坐车窗边看风景，自然光线。', purposes: ['氛围感', '短视频'], targetUser: '泛人群' },
  { motion: 'scenery', scene: 'travel', title: '看风景影片', description: '站着或走着看风景，无口播。', purposes: ['背景素材'], targetUser: '泛人群' },
];

export const DEFAULT_TALKING_SCRIPT =
  '很多人不是服务不好，是别人看完主页不知道你能帮谁。其实先不用想太复杂，把「你帮谁、解决什么问题」说清楚，私信就会多很多。';

export const TALKING_VIDEO_CARD = {
  title: '角色口播影片',
  description: '生成 40-60 秒真人感口播，绑定角色五官、脸型、声音与语气。',
  purposes: ['抖音', '视频号', '私域信任', '客户跟进'],
  targetUser: '需要建立信任后再转化的人',
  duration: '40-60秒',
};

/** 今日素材包推荐项（规格 §2 / §7） */
export const DAILY_PACK_RECOMMENDATIONS = [
  { n: 1, title: '生成 3 张角色生活照', purpose: '朋友圈铺垫 / 建立真人感' },
  { n: 2, title: '生成 1 条朋友路拍无声影片', purpose: '短视频背景 / 私信破冰前铺垫' },
  { n: 3, title: '生成 1 条 40 秒角色口播', purpose: '抖音 / 视频号 / 私域信任' },
  { n: 4, title: '生成 3 条私信破冰话术', purpose: '主动开发与客户回复' },
  { n: 5, title: '同步素材到回复工具', purpose: '业务回复客户时直接调用' },
] as const;

export const EFFECT_FEEDBACK_OPTIONS = [
  { value: 'replied', label: '有人回复' },
  { value: 'no_reply', label: '没人回复' },
  { value: 'asked_for_asset', label: '有人要资料' },
  { value: 'added_private', label: '有人加私域' },
  { value: 'booked_diagnosis', label: '有人愿意诊断' },
  { value: 'converted', label: '有人成交' },
  { value: 'unnatural', label: '不自然' },
  { value: 'too_salesy', label: '太像广告' },
  { value: 'rejected', label: '对方拒绝' },
] as const;

export const PLATFORM_OPTIONS = [
  { value: 'wechat', label: '微信' },
  { value: 'douyin', label: '抖音' },
  { value: 'xiaohongshu', label: '小红书' },
  { value: 'dm', label: '私信' },
  { value: 'comment', label: '评论' },
] as const;

export const TEXT_MATERIAL_CARDS = [
  {
    type: 'moments' as const,
    title: '朋友圈素材',
    description: '像普通人随手发的生活感悟，可轻微带入观察但不硬推。',
    purposes: ['朋友圈铺垫', '建立真人感'],
    targetUser: '想先建立信任的人',
    defaultCount: 2,
  },
  {
    type: 'dm_opener' as const,
    title: '私信开场',
    description: '低压力破冰，让对方愿意回一句。每条都有下一句追问。',
    purposes: ['主动开发', '客户回复'],
    targetUser: '有服务但主页说不清的人',
    defaultCount: 3,
  },
  {
    type: 'comment_icebreaker' as const,
    title: '评论破冰',
    description: '在对方笔记或视频下暖场，不像硬广评论。',
    purposes: ['评论暖场', '引流到私信'],
    targetUser: '陌生人互动',
    defaultCount: 2,
  },
] as const;

export const RESULT_LABELS: Record<string, string> = {
  replied: '有人回复',
  no_reply: '没人回复',
  asked_for_asset: '有人要资料',
  added_private: '有人加私域',
  booked_diagnosis: '有人愿意诊断',
  converted: '有人成交',
  unnatural: '不自然',
  too_salesy: '太像广告',
  rejected: '对方拒绝',
  got_reply: '有人回复',
  got_dm: '有人私信',
  got_lead: '有人领资料',
  got_deal: '有人成交',
};
